import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { ReactSVG } from 'react-svg'
import { useMediaQuery } from 'usehooks-ts'

import PaymentModal from '@entities/Order/ui/PaymentModal/PaymentModal'

import { useAuth } from '@features/auth'
import { useCartStore } from '@features/cart/model/useCartStore'
import { useModalStore } from '@features/modal'
import { useManagePaymentMethods } from '@features/payment/hooks/useManagePaymentMethods'

import { URL_CART, URL_PAGE_CATALOG } from '@shared/contants'
import Button from '@shared/ui/button'
import DotsLoader from '@shared/ui/dots-loader'
import Wrapper from '@shared/ui/wrapper'
import { formatPrice } from '@shared/uttils'
import { formatGramsToHumanWeight } from '@shared/uttils/formatGramsToHumanWeight'
import getNoun from '@shared/uttils/getNoun'

import styles from './styles.module.scss'



interface OrderAmountProps {
  onSubmit: (paymentId: string) => void;
  loading: boolean;
}

export default function OrderAmount ({ onSubmit, loading }: OrderAmountProps) {
  const navigate = useNavigate()
  const [ checkoutButtonLoading, setCheckoutButtonLoading ] =
    useState<boolean>(false)
  const isMobile = useMediaQuery('(max-width: 768px)')
  const { t: tp } = useTranslation('product')
  const { t: to } = useTranslation('order')
  const { t: tc } = useTranslation('common')
  const { t: tca } = useTranslation('cart')
  const { t: tn } = useTranslation('navigation')
  const {
    getTotalQuantity,
    cartInfoData: cartTotal,
    cartInfoLoading: cartLoading,
    checkProductsInStock,
    removeZeroAmountItemsInCurrentCart,
    syncCart,
  } = useCartStore()
  const { openModal } = useModalStore()
  const [ selected, setSelected ] = useState<string | null>(null)
  const { paymentMethods, defaultCard } = useManagePaymentMethods()
  const { isAuthorized } = useAuth()

  useEffect(() => {
    if (paymentMethods.length === 0) {
      setSelected('new-card')
    }
    else if (defaultCard?.id) {
      setSelected(defaultCard.id)
    }
  }, [ defaultCard, paymentMethods ])

  const selectedCard = useMemo(() => {
    return paymentMethods?.find(
      (method) => method?.type === 'card' && method.id === selected,
    )
  }, [ paymentMethods, selected ])

  const cardIcons: Record<string, string> = {
    master: 'payments/mastercard',
    visa: 'payments/visa',
  }

  const iconSrc = `/icons/${cardIcons[selectedCard?.card ?? ''] ?? 'card'}.svg`

  const TOTAL = getTotalQuantity()

  const handleClick = async () => {
    setCheckoutButtonLoading(true)

    const hasChanges = await checkProductsInStock()
    await removeZeroAmountItemsInCurrentCart(isAuthorized)
    await syncCart()

    setCheckoutButtonLoading(false)

    if (hasChanges) {
      navigate(URL_CART)
    }
    else {
      onSubmit(selected ?? 'new-card')
    }
  }

  return !loading || !cartLoading ? (
    <div className={styles.root}>
      <Wrapper className={styles.root__wrapper}>
        {cartTotal?.products?.length ? (
          <>
            <div className={styles.root__top}>
              <h3>
                {TOTAL}{' '}
                {getNoun(
                  TOTAL as number,
                  tp('product.1'),
                  tp('product.2'),
                  tp('product.3'),
                )}
              </h3>
              <p>
                ~{' '}
                {formatGramsToHumanWeight({
                  weight: Number(cartTotal?.totalWeight),
                  t: tc,
                })}
              </p>
            </div>
            <div className={styles.root__body}>
              <div className={styles.root__item}>
                <p>{tc('delivery')}:</p>
                <span>{formatPrice(cartTotal.delivery?.total as number)}</span>
              </div>
              <div className={styles.root__line} />
              <div
                className={classNames(
                  styles.root__item,
                  styles['root__item--total'],
                )}
              >
                <p>{to('order.total')}:</p>
                <span>{formatPrice(cartTotal.totals?.total as number)}</span>
              </div>
              <div className={styles.root__item}>
                <p>{to('order.discount')}:</p>
                <span>
                  − {formatPrice(cartTotal.totals?.baseSale as number)}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className={styles.root__empty}>
            {tca('empty_cart')}
            <Button to={URL_PAGE_CATALOG}>{tn('go_to_catalog')}</Button>
          </div>
        )}
        <Button
          onClick={() => {
            openModal(
              'orderPayments',
              <PaymentModal
                onChoose={(cardId: string) => setSelected(cardId)}
                current={selected}
              />,
              {
                title: to('order.payments-add'),
              },
            )
          }}
          size={!isMobile ? 'large' : 'medium'}
          className={styles.root__button}
          theme="grey"
        >
          <ReactSVG src={iconSrc} />
          {selected === 'new-card' || !selectedCard
            ? to('new_card_payment')
            : `${to('card')} ****${selectedCard.last}`}
          <ReactSVG src="/icons/arrow-right.svg" />
        </Button>
        <Button
          type="submit"
          disabled={!cartTotal?.products?.length || loading}
          onClick={handleClick}
          size={!isMobile ? 'large' : 'medium'}
          className={styles.root__button}
        >
          {checkoutButtonLoading ? <DotsLoader /> : to('checkout_button')}
        </Button>
      </Wrapper>
    </div>
  ) : (
    <SkeletonTheme>
      <Skeleton
        count={1}
        width={'100%'}
        height={300}
        style={{
          borderRadius: 32,
        }}
      />
    </SkeletonTheme>
  )
}
